import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UploadSimple, FileText, Trash, TextAa, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Section } from "@/components/builder/IdentitySection";
import { api, errMsg, fmtDate } from "@/lib/api";

const ACCEPT = ".pdf,.txt,.md,.csv,.tsv,.json,.jsonl,.docx,.html,.htm,.xml";
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const KnowledgeDialog = ({ open, onOpenChange, agentId, onDone }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/agents/${agentId}/knowledge`, { title, content });
      toast.success("Conocimiento añadido");
      setTitle(""); setContent("");
      onDone(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-sm border-white/10 bg-[#0f0f0f] text-white" data-testid="knowledge-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Añadir conocimiento manual</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><label className="label">Título</label><input value={title} onChange={(e) => setTitle(e.target.value)} className="field" placeholder="Ej.: Horarios y tarifas 2026" data-testid="knowledge-title-input" /></div>
          <div><label className="label">Contenido</label><textarea value={content} onChange={(e) => setContent(e.target.value)} rows={10} className="field" placeholder="Pega aquí FAQs, políticas, catálogos, procedimientos…" data-testid="knowledge-content-input" /></div>
          <button onClick={save} disabled={saving || !title.trim() || !content.trim()} className="btn-primary w-full" data-testid="knowledge-save-button">{saving ? "Procesando…" : "Entrenar con este contenido"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TrainingSection = ({ agent, form, set, onChanged }) => {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [kOpen, setKOpen] = useState(false);
  const { data: docs = [] } = useQuery({ queryKey: ["docs", agent.agent_id], queryFn: async () => (await api.get(`/agents/${agent.agent_id}/documents`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["docs", agent.agent_id] }); onChanged?.(); };

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).slice(0, 10).forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const { data } = await api.post(`/agents/${agent.agent_id}/documents`, fd);
      data.results.forEach((r) => (r.ok ? toast.success(`${r.filename}: ${r.document.chunk_count} fragmentos${r.document.example_count ? ` · ${r.document.example_count} ejemplos` : ""}`) : toast.error(`${r.filename}: ${r.error}`)));
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };
  const remove = async (id) => {
    try { await api.delete(`/agents/${agent.agent_id}/documents/${id}`); toast.success("Fuente eliminada"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const totals = docs.reduce((a, d) => ({ chunks: a.chunks + d.chunk_count, ex: a.ex + d.example_count, chars: a.chars + d.char_count }), { chunks: 0, ex: 0, chars: 0 });

  return (
    <Section id="training" title="Entrenamiento" desc="Dale a tu agente el conocimiento de tu negocio. Recupera los fragmentos relevantes en cada respuesta y aprende de tus ejemplos.">
      <div className="flex items-center justify-between border border-white/10 bg-[#080808] p-4">
        <div><p className="text-sm font-semibold">Usar base de conocimiento</p><p className="text-xs text-zinc-500">Consulta los documentos y ejemplos en cada respuesta.</p></div>
        <Switch checked={form.use_knowledge} onCheckedChange={(v) => set("use_knowledge", v)} data-testid="builder-use-knowledge-switch" />
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer border border-dashed p-10 ${drag ? "border-blue-500 bg-blue-600/5" : "border-white/20 hover:border-blue-500"} ${uploading ? "thinking-beam" : ""}`}
        style={{ transition: "border-color 200ms, background-color 200ms" }} data-testid="training-dropzone"
      >
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files)} data-testid="training-file-input" />
        <UploadSimple size={28} className="text-blue-500" />
        <p className="mt-4 font-display text-lg font-bold">{uploading ? "Procesando archivos…" : "Arrastra archivos o haz clic para subir"}</p>
        <p className="mt-1 font-mono text-xs text-zinc-500">PDF · DOCX · TXT · MD · CSV · TSV · JSON · JSONL · HTML · XML — máx. 15 MB, 10 a la vez</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <button onClick={() => setKOpen(true)} className="btn-ghost !py-2" data-testid="training-add-text-button"><TextAa size={16} /> Añadir texto manual</button>
      </div>
      <div className="flex gap-3 border border-cyan-500/20 bg-cyan-500/5 p-4 text-xs leading-relaxed text-zinc-400">
        <Info size={16} className="mt-0.5 shrink-0 text-cyan-400" />
        <p>Los archivos <b className="text-white">JSON/JSONL</b> con pares <code className="text-cyan-300">prompt/completion</code>, <code className="text-cyan-300">input/output</code>, <code className="text-cyan-300">question/answer</code>, <code className="text-cyan-300">instruction/output</code> o formato <code className="text-cyan-300">{"{messages:[…]}"}</code> se convierten en <b className="text-white">ejemplos de entrenamiento</b> que el agente imita. El resto se indexa como conocimiento.</p>
      </div>
      <div className="grid grid-cols-3 border border-white/10">
        {[["Fuentes", docs.length], ["Fragmentos", totals.chunks], ["Ejemplos", totals.ex]].map(([l, v]) => (
          <div key={l} className="border-r border-white/10 p-4 last:border-r-0" data-testid={`training-stat-${l.toLowerCase()}`}><p className="font-mono text-xl font-semibold">{v}</p><p className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</p></div>
        ))}
      </div>
      {docs.length > 0 && (
        <div className="border border-white/10" data-testid="training-doc-list">
          {docs.map((d) => (
            <div key={d.doc_id} className="flex items-center gap-3 border-b border-white/5 px-4 py-3 last:border-b-0 hover:bg-white/[0.02]" data-testid={`training-doc-${d.doc_id}`}>
              {d.source === "text" ? <TextAa size={18} className="shrink-0 text-cyan-400" /> : <FileText size={18} className="shrink-0 text-blue-500" />}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{d.filename}</p>
                <p className="font-mono text-[11px] text-zinc-500">{d.ext?.toUpperCase()} · {kb(d.size || d.char_count)} · {d.chunk_count} frag. {d.example_count ? `· ${d.example_count} ejemplos ` : ""}· {fmtDate(d.created_at)}</p>
              </div>
              <button onClick={() => remove(d.doc_id)} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`training-doc-delete-${d.doc_id}`}><Trash size={16} /></button>
            </div>
          ))}
        </div>
      )}
      <KnowledgeDialog open={kOpen} onOpenChange={setKOpen} agentId={agent.agent_id} onDone={refresh} />
    </Section>
  );
};
